import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import * as characterService from '../../services/characters'
import * as gameSettingsService from '../../services/game_settings'
import * as gameRoleService from '../../services/game_roles'
import Modal from '../components/Modal'
import './Settings.css'
import { useDispatch, useSelector } from 'react-redux'
import { destroyPixel, changePixelCharacter } from '../../actions/pixels'
import { addGameSetting, updateGameSetting } from '../../actions'
import { addGameRole, destroyGameRole } from '../../actions/game_roles'
import { PIXEL_COORDINATOR } from '../../constants/action-types'
import store from '../../store'
import { Color } from '@systemic-games/pixels-web-connect'
import { PIXEL_COLORS, getColorOptions } from '../../lib/pixellib'
import { usePixelManager } from '../../contexts/PixelContext'

import {
  closeSettings,
  updateNewCharacterName,
  updateNewCharacterAvatarUrl,
  regenerateViewerToken,
} from '../../actions'

const Settings = (props) => {
  const {
    characters,
    newCharacterName,
    newCharacterAvatarUrl,
    characterId,
    targetCharacterId,
    token,
    isOwner,
    game,
    gameRoles,
  } = props

  // Use the target character ID if it exists and user is owner, otherwise use the store's characterId
  const activeCharacterId = (targetCharacterId && isOwner) ? targetCharacterId : characterId
  const isOwnCharacter = activeCharacterId === characterId

  // Get the active character's data
  const activeCharacter = characters[activeCharacterId]

  // Local state for editing other characters
  const [localName, setLocalName] = useState(activeCharacter.name)
  const [localAvatarUrl, setLocalAvatarUrl] = useState(activeCharacter.avatar_url)

  // Update local state when active character changes
  useEffect(() => {
    setLocalName(activeCharacter.name)
    setLocalAvatarUrl(activeCharacter.avatar_url)
  }, [activeCharacterId])

  const handleClose = () => {
    props.closeSettings()
  }

  const handleNewCharacterNameChange = (e) => {
    if (isOwnCharacter) {
      props.updateNewCharacterName(e.target.value)
    } else {
      setLocalName(e.target.value)
    }
  }

  const handleCharacterNameSave = () => {
    const nameToSave = isOwnCharacter ? newCharacterName : localName
    characterService.updateName(nameToSave, activeCharacterId, token)
  }

  const handleNewCharacterAvatarUrlChange = (e) => {
    if (isOwnCharacter) {
      props.updateNewCharacterAvatarUrl(e.target.value)
    } else {
      setLocalAvatarUrl(e.target.value)
    }
  }

  const handleCharacterAvatarUrlSave = () => {
    const urlToSave = isOwnCharacter ? newCharacterAvatarUrl : localAvatarUrl
    characterService.updateAvatarUrl(urlToSave, activeCharacterId, token)
  }

  async function handleCharacterDelete() {
    let msg = 'Are you sure you want to delete your character FOREVER?\n\n'
    msg += 'This means:\n'
    msg += '  All shortcuts deleted!\n'
    msg += '  All roll history deleted!\n'
    msg += '  You will have to create a new character to rejoin this game!\n'

    if (window.confirm(msg)) {
      characterService.destroy(activeCharacterId, token)
    }
  }

  const characterPrivateToken = characters ? characters[activeCharacterId].private_token : null

  return(
    <Modal title='Settings' onClose={handleClose}>
      <SettingsControl
        displayName='Character Name'
        value={isOwnCharacter ? newCharacterName : localName}
        onChangeFn={handleNewCharacterNameChange}
        onSaveFn={handleCharacterNameSave}
      />

      <SettingsControl
        displayName='Avatar URL'
        value={isOwnCharacter ? newCharacterAvatarUrl : localAvatarUrl}
        onChangeFn={handleNewCharacterAvatarUrlChange}
        onSaveFn={handleCharacterAvatarUrlSave}
      />
      {isOwner && isOwnCharacter && (
        <div className='Settings__control'>
          <label className='Settings__label'>Viewer Link</label>
          <input
            type='text'
            className='Settings__input'
            value={game?.private_token ? `${window.location.origin}/viewer/${game.private_token}` : ''}
            readOnly
            style={{ width: game?.private_token ? `${window.location.origin.length + game.private_token.length + 10}ch` : 'auto' }}
          />
          <button
            className='Settings__button'
            onClick={() => navigator.clipboard.writeText(`${window.location.origin}/viewer/${game.private_token}`)}
            title='Copy to clipboard'
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, width: '32px' }}
          >
            <svg
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <rect x='9' y='9' width='13' height='13' rx='2' ry='2'></rect>
              <path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'></path>
            </svg>
          </button>
          <button
            className='Settings__button'
            onClick={() => props.regenerateViewerToken(game.id)}
          >
            Regenerate
          </button>
        </div>
      )}

      {/* Roles Section - Only show for GMs when viewing other characters */}
      {isOwner && !isOwnCharacter && (
        <SettingsRolesSection
          character={activeCharacter}
          gameRoles={gameRoles}
          gameId={game.id}
          token={token}
        />
      )}

      {/* Pixel Section - Show for character owner, GMs, and pixel coordinators */}
      {(isOwnCharacter || isOwner || props.canCoordinatePixels) && (
        <SettingsPixelSection
          characterPrivateToken={characterPrivateToken}
          characterId={activeCharacterId}
          isOwner={isOwner}
          game={game}
          canCoordinatePixels={props.canCoordinatePixels}
          regenerateViewerToken={props.regenerateViewerToken}
        />
      )}

      {isOwnCharacter && (
        <div className='Settings__control'>
          <button
            className='Settings__button--warning'
            onClick={handleCharacterDelete}
          >Delete my character for-evar!</button>
        </div>
      )}
    </Modal>
  )
}

const mapStateToProps = (state /*, ownProps*/) => {
  // Check if current user can coordinate pixels
  const currentUserId = state.characters.list[state.characterId]?.user_id
  const canCoordinatePixels = Object.values(state.game_roles.list).some(
    role => role.user_id === currentUserId && role.role_type === PIXEL_COORDINATOR
  )

  return {
    characters: state.characters.list,
    characterId: state.characterId,
    targetCharacterId: state.client.targetCharacterId,
    newCharacterName: state.client.newCharacterName,
    newCharacterAvatarUrl: state.client.newCharacterAvatarUrl,
    token: state.client.token,
    ownerId: state.ownerId,
    isOwner: state.user.is_owner,
    game: state.game,
    gameRoles: state.game_roles.list,
    canCoordinatePixels,
  }
}

const mapDispatchToProps = {
  closeSettings,
  updateNewCharacterName,
  updateNewCharacterAvatarUrl,
  regenerateViewerToken,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Settings)

const SettingsControl = ({displayName, value, onChangeFn, onSaveFn}) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onSaveFn()
    }
  }

  return(
    <div className='Settings__control'>
      <label className='Settings__label'>{displayName}</label>
      <input type='text'
        className='Settings__input'
        value={value}
        onChange={onChangeFn}
        onKeyDown={handleKeyDown}
      />
      <button className='Settings__button' onClick={onSaveFn}>Save</button>
    </div>
  )
}

const SettingsPixelSection = ({ characterPrivateToken, characterId, isOwner, game, canCoordinatePixels }) => {
  const url = `${window.location.origin}/pixels/roll/c/${characterPrivateToken}`;
  const dispatch = useDispatch();
  const pixels = useSelector(state => Object.values(state.pixels.list));
  const characters = useSelector(state => state.characters.list);
  const gameSettings = useSelector(state => state.game_settings.settings);
  const { pairPixel } = usePixelManager();

  const [onDeckColor, setOnDeckColor] = useState(PIXEL_COLORS.YELLOW.value);
  const [yourTurnColor, setYourTurnColor] = useState(PIXEL_COLORS.GREEN.value);

  // Check if user can manage pixel characters (GM or pixel coordinator)
  const canManagePixelCharacters = isOwner || canCoordinatePixels;

  const handleColorChange = async (setting, color) => {
    const pixelSettings = gameSettings?.pixels?.entry || {};
    const newSettings = {
      ...pixelSettings,
      [setting]: color
    };

    try {
      if (gameSettings?.pixels) {
        // Update existing settings
        const response = await gameSettingsService.update(
          {
            key: 'pixels',
            entry: newSettings
          },
          gameSettings.pixels.id,
          game.id,
          store.getState().client.token
        );
        dispatch(updateGameSetting(response));
      } else {
        // Create new settings
        const response = await gameSettingsService.save(
          {
            key: 'pixels',
            entry: newSettings
          },
          game.id,
          store.getState().client.token
        );
        dispatch(addGameSetting(response));
      }
    } catch (error) {
      console.error('Error saving color settings:', error);
    }
  };

  useEffect(() => {
    const pixelSettings = gameSettings?.pixels?.entry || {};

    // Set default colors if none exist
    if (!pixelSettings.on_deck_color && !gameSettings?.pixels) {
      const yellowValue = PIXEL_COLORS.YELLOW.value;
      handleColorChange('on_deck_color', yellowValue);
      setOnDeckColor(yellowValue);
    } else if (pixelSettings.on_deck_color) {
      // Handle both string and number values
      const color = typeof pixelSettings.on_deck_color === 'object'
        ? PIXEL_COLORS.YELLOW.value // Default to yellow if we get an object
        : typeof pixelSettings.on_deck_color === 'string'
          ? parseInt(pixelSettings.on_deck_color, 16)
          : pixelSettings.on_deck_color; // Use the number directly if it's already a number
      if (!isNaN(color)) {
        setOnDeckColor(color);
      }
    }

    if (!pixelSettings.your_turn_color && !gameSettings?.pixels) {
      const greenValue = PIXEL_COLORS.GREEN.value;
      handleColorChange('your_turn_color', greenValue);
      setYourTurnColor(greenValue);
    } else if (pixelSettings.your_turn_color) {
      // Handle both string and number values
      const color = typeof pixelSettings.your_turn_color === 'object'
        ? PIXEL_COLORS.GREEN.value // Default to green if we get an object
        : typeof pixelSettings.your_turn_color === 'string'
          ? parseInt(pixelSettings.your_turn_color, 16)
          : pixelSettings.your_turn_color; // Use the number directly if it's already a number
      if (!isNaN(color)) {
        setYourTurnColor(color);
      }
    }
  }, [gameSettings]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(url);
  };

  const configurePixel = async () => {
    try {
      await pairPixel(characterId);
    } catch (error) {
      // Only log if it's not a user cancellation
      if (error.name !== 'NotFoundError') {
        console.error('Error configuring pixel:', error);
      }
    }
  };

  const colorOptions = getColorOptions();

  return (
    <div className='Settings__section'>
      <h3 className='Settings__sectionTitle'>Pixel Settings</h3>
      <div className='Settings__control'>
        <label className='Settings__label'>Web Hook</label>
        <input
          type='text'
          className='Settings__input'
          value={url}
          readOnly
          style={{ width: `${url.length + 2}ch` }}
        />
        <button
          className='Settings__button'
          onClick={copyToClipboard}
          title='Copy to clipboard'
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, width: '32px' }}
        >
          <svg
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <rect x='9' y='9' width='13' height='13' rx='2' ry='2'></rect>
            <path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'></path>
          </svg>
        </button>
      </div>
      <div className='Settings__control'>
        <button
          className='Settings__button'
          onClick={configurePixel}
        >
          Configure Pixel
        </button>
      </div>
      {isOwner && (
        <>
          <div className='Settings__control'>
            <label className='Settings__label'>On Deck Color</label>
            <select
              className='Settings__input'
              value={onDeckColor}
              onChange={(e) => {
                const color = parseInt(e.target.value);
                if (!isNaN(color)) {
                  setOnDeckColor(color);
                  handleColorChange('on_deck_color', color);
                }
              }}
            >
              {colorOptions.map(option => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className='Settings__control'>
            <label className='Settings__label'>Your Turn Color</label>
            <select
              className='Settings__input'
              value={yourTurnColor}
              onChange={(e) => {
                const color = parseInt(e.target.value);
                if (!isNaN(color)) {
                  setYourTurnColor(color);
                  handleColorChange('your_turn_color', color);
                }
              }}
            >
              {colorOptions.map(option => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      {pixels.length > 0 && (
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
          {pixels.map(pixel => (
            <li key={pixel.pixelId} style={{ display: 'flex', alignItems: 'center' }}>
              Character: {canManagePixelCharacters ? (
                <select
                  value={pixel.characterId}
                  onChange={e => dispatch(changePixelCharacter(pixel.pixelId, e.target.value))}
                  style={{ marginRight: 8 }}
                >
                  {Object.values(characters).map(char => (
                    <option key={char.id} value={char.id}>{char.name}</option>
                  ))}
                </select>
              ) : (
                characters[pixel.characterId]?.name || "Unknown"
              )}
              — {pixel.name} ({pixel.dieType}) - Pixel ID: {pixel.pixelId} (last roll: <strong>{pixel.lastRoll}</strong>)
              <button
                style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                title="Blink Pixel"
                onClick={async () => {
                  if (pixel.pixel) {
                    await pixel.pixel.blink(Color.green);
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                </svg>
              </button>
              <button
                style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                title="Remove Pixel"
                onClick={async () => {
                  if (pixel.pixel) {
                    await pixel.pixel.blink(Color.red);
                    await pixel.pixel.disconnect();
                  }
                  dispatch(destroyPixel(pixel.pixelId));
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const descriptions = {
  [PIXEL_COORDINATOR]: 'Can configure pixel dice for any character in this game'
}

const getRoleDescription = (roleType) => {
  return descriptions[roleType] || roleType
}

const SettingsRolesSection = ({ character, gameRoles, gameId, token }) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)

  // Get roles for this character's user
  const userRoles = Object.values(gameRoles).filter(role => role.user_id === character.user_id)

  const handleRoleToggle = async (roleType, hasRole) => {
    try {
      setLoading(true)
      if (hasRole) {
        // Find the role to delete
        const roleToDelete = userRoles.find(role => role.role_type === roleType)
        if (roleToDelete) {
          const result = await gameRoleService.destroy(roleToDelete.id, gameId)
          dispatch(destroyGameRole(result))
        }
      } else {
        const result = await gameRoleService.create(character.user_id, gameId, roleType)
        dispatch(addGameRole(result))
      }
    } catch (error) {
      console.error('Error toggling role:', error)
      alert('Failed to update role. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='Settings__section'>
      <h3 className='Settings__sectionTitle'>Roles</h3>
      <p className='Settings__sectionDescription'>
        Manage roles for {character.name} (@{character.username})
      </p>

      <div className='Settings__roles'>
        {Object.keys(descriptions).map((roleType) => {
          const hasRole = userRoles.some(role => role.role_type === roleType);
          const roleName = roleType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

          return (
            <div className='Settings__role' key={roleType}>
              <label className='Settings__roleLabel'>
                <input
                  type='checkbox'
                  checked={hasRole}
                  onChange={() => handleRoleToggle(roleType, hasRole)}
                  disabled={loading}
                />
                <span className='Settings__roleName'>{roleName}</span>
              </label>
              <span className='Settings__roleDescription'>
                {getRoleDescription(roleType)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  )
}
